import { Component, signal, HostListener, ElementRef, ViewChild, effect, computed, untracked, EffectRef, OnDestroy } from '@angular/core';
import { CommonModule } from '@angular/common';
import { DevToolsPanelComponent } from '../dev-tools-panel/dev-tools-panel.component';

@Component({
  selector: 'app-draggable-dev-tools',
  standalone: true,
  imports: [CommonModule, DevToolsPanelComponent],
  templateUrl: './draggable-dev-tools.component.html',
  styleUrl: './draggable-dev-tools.component.scss'
})
export class DraggableDevToolsComponent implements OnDestroy {
  // Constants
  private readonly MIN_WIDTH = 400;
  private readonly MIN_HEIGHT = 300;
  private readonly DEFAULT_WIDTH = 1200;
  private readonly DEFAULT_HEIGHT = 700;
  private readonly DEFAULT_X = 100;
  private readonly DEFAULT_Y = 100;
  private readonly HEADER_HEIGHT = 44; // Modal header height

  @ViewChild('devToolsModal') modalElement!: ElementRef;

  isOpen = signal(false);
  isMinimized = signal(false);
  isMaximized = signal(false);

  // Position and size
  position = signal({ x: this.DEFAULT_X, y: this.DEFAULT_Y });
  size = signal({ width: this.DEFAULT_WIDTH, height: this.DEFAULT_HEIGHT });

  // Drag state
  private isDragging = false;
  private dragStartX = 0;
  private dragStartY = 0;
  private dragStartPosition = { x: 0, y: 0 };

  // Resize state
  private isResizing = false;
  private resizeDirection = '';
  private resizeStartX = 0;
  private resizeStartY = 0;
  private resizeStartSize = { width: 0, height: 0 };
  private resizeStartPosition = { x: 0, y: 0 };

  // Saved state for maximize/restore
  private savedPosition = { x: this.DEFAULT_X, y: this.DEFAULT_Y };
  private savedSize = { width: this.DEFAULT_WIDTH, height: this.DEFAULT_HEIGHT };

  // Computed available height for content (modal height - header height)
  contentHeight = computed(() => {
    const modalHeight = this.size().height;
    return modalHeight - this.HEADER_HEIGHT;
  });

  // Store effect reference for cleanup
  private centerEffectRef?: EffectRef;

  constructor() {
    // Center modal when first opened (using untracked to avoid re-centering on size changes)
    this.centerEffectRef = effect(() => {
      if (this.isOpen()) {
        untracked(() => this.centerModal());
      }
    });
  }

  ngOnDestroy(): void {
    // Clean up effect to prevent memory leaks
    this.centerEffectRef?.destroy();
  }

  toggleDevTools() {
    this.isOpen.set(!this.isOpen());
  }

  closeDevTools() {
    this.isOpen.set(false);
    this.isMinimized.set(false);
    this.isMaximized.set(false);
  }


  centerModal() {
    const windowWidth = window.innerWidth;
    const windowHeight = window.innerHeight;
    const modalWidth = this.size().width;
    const modalHeight = this.size().height;

    this.position.set({
      x: (windowWidth - modalWidth) / 2,
      y: (windowHeight - modalHeight) / 2
    });
  }

  minimizeModal() {
    this.isMinimized.set(true);
  }

  restoreModal() {
    this.isMinimized.set(false);
  }

  maximizeModal() {
    if (this.isMaximized()) {
      // Restore
      this.position.set(this.savedPosition);
      this.size.set(this.savedSize);
      this.isMaximized.set(false);
    } else {
      // Maximize
      this.savedPosition = { ...this.position() };
      this.savedSize = { ...this.size() };

      this.position.set({ x: 0, y: 0 });
      this.size.set({
        width: window.innerWidth,
        height: window.innerHeight
      });
      this.isMaximized.set(true);
    }
  }

  // Drag functionality
  startDrag(event: MouseEvent) {
    if (this.isMaximized()) return;

    this.isDragging = true;
    this.dragStartX = event.clientX;
    this.dragStartY = event.clientY;
    this.dragStartPosition = { ...this.position() };

    event.preventDefault();
  }

  @HostListener('document:mousemove', ['$event'])
  onMouseMove(event: any) {
    if (this.isDragging) {
      const deltaX = event.clientX - this.dragStartX;
      const deltaY = event.clientY - this.dragStartY;

      this.position.set({
        x: this.dragStartPosition.x + deltaX,
        y: this.dragStartPosition.y + deltaY
      });
    }

    if (this.isResizing) {
      this.handleResize(event);
    }
  }

  @HostListener('document:mouseup')
  onMouseUp() {
    this.isDragging = false;
    this.isResizing = false;
    this.resizeDirection = '';
  }

  // Resize functionality
  startResize(event: MouseEvent, direction: string) {
    if (this.isMaximized()) return;

    this.isResizing = true;
    this.resizeDirection = direction;
    this.resizeStartX = event.clientX;
    this.resizeStartY = event.clientY;
    this.resizeStartSize = { ...this.size() };
    this.resizeStartPosition = { ...this.position() };

    event.preventDefault();
    event.stopPropagation();
  }

  private handleResize(event: MouseEvent) {
    const deltaX = event.clientX - this.resizeStartX;
    const deltaY = event.clientY - this.resizeStartY;

    let newWidth = this.resizeStartSize.width;
    let newHeight = this.resizeStartSize.height;
    let newX = this.resizeStartPosition.x;
    let newY = this.resizeStartPosition.y;

    // Handle horizontal resize
    if (this.resizeDirection.includes('e')) {
      newWidth = Math.max(this.MIN_WIDTH, this.resizeStartSize.width + deltaX);
    }
    if (this.resizeDirection.includes('w')) {
      newWidth = Math.max(this.MIN_WIDTH, this.resizeStartSize.width - deltaX);
      newX = this.resizeStartPosition.x + (this.resizeStartSize.width - newWidth);
    }

    // Handle vertical resize
    if (this.resizeDirection.includes('s')) {
      newHeight = Math.max(this.MIN_HEIGHT, this.resizeStartSize.height + deltaY);
    }
    if (this.resizeDirection.includes('n')) {
      newHeight = Math.max(this.MIN_HEIGHT, this.resizeStartSize.height - deltaY);
      newY = this.resizeStartPosition.y + (this.resizeStartSize.height - newHeight);
    }

    this.size.set({ width: newWidth, height: newHeight });
    this.position.set({ x: newX, y: newY });
  }
}